import { useEffect } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import { generateEDDLabel } from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import { getCurrentFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import ZipCodeButton from '../zipCodeButton'

import styles from './eddMessageContainer.module.scss'

interface EDDMessageContainerProps {
  eddDetail: EDDDetails | undefined
  product: Product | undefined
  selectedInternalVariantId: number[] | undefined
}

export function EDDMessageContainer({ eddDetail, product, selectedInternalVariantId }: EDDMessageContainerProps) {
  const { zipCode } = useZipCode()
  const selectedVariant = selectedInternalVariantId?.[0]?.toString()
  useEffect(() => {
    if (eddDetail?.showEDD) {
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
        action: 'product_page_edd',
        category: 'edd',
        context: {
          product_id: product?.shopifyProductId,
          findingMethodAggregate: currentFindingMethod?.findingMethodAggregate,
          findingMethodGranular: currentFindingMethod?.findingMethodGranular,
          findingMethodId: currentFindingMethod?.findingMethodId,
          edd_standard_eligible: eddDetail.type === 'LB_STANDARD',
          edd_express_eligible: eddDetail.type === 'LB_SUPER',
          edd_max_days: eddDetail.max_days,
          edd_min_days: eddDetail.min_days,
          productProperties: product && getProductPropertiesForTracking(product, selectedVariant),
        },
      })
    }
  }, [eddDetail?.showEDD, eddDetail?.type, eddDetail?.max_days, eddDetail?.min_days, product?.id, selectedVariant])

  if (!eddDetail?.showEDD) return null

  if (!zipCode) {
    return (
      <div className={styles.eddMessageWrapper}>
        <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>Enter your zip</ZipCodeButton> to see when it will arrive
      </div>
    )
  }

  if (eddDetail.isExpressShipping) {
    return (
      <div className={styles.eddMessageWrapper}>
        {generateEDDLabel(eddDetail)} with priority shipping to{' '}
        <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
      </div>
    )
  }

  return (
    <div className={styles.eddMessageWrapper}>
      {generateEDDLabel(eddDetail)}. Ships to{' '}
      <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
    </div>
  )
}
